// ---------------------------------------------------------------------------
// cogibulle diffusion — Construction des documents « miroir hub » depuis un
// payload de synchronisation (format cogibulle-diffusion-game).
//
// Module PUR et isomorphe : aucune écriture en base ici. Il est utilisé par :
//   - server/serveur.js (installe_miroir_hub_compatible) : ingestion d'une
//     version poussée par le hub, insertion en Mongo ;
//   - client/imports/runtime_embarque.js (appli native) : hydratation de
//     Minimongo depuis le manifest embarqué, hors ligne.
// Toute évolution du schéma des documents miroir doit se faire ICI pour que
// serveur de diffusion et appli embarquée restent alignés.
// ---------------------------------------------------------------------------

import { normalise_catalogue_diapos_taille_pct } from './projet_publications_settings.js';
import { normalise_communication_diffusion_projet } from './projet_communication_settings.js';

// Doit rester aligné avec client/scene/scene.js (const locale identique).
export const SVG_RUNTIME_COMPILATION_VERSION = 3;

function clone_simple_miroir (pValeur, pDefaut = null)
  {
    if (pValeur === undefined || pValeur === null) return pDefaut;
    return JSON.parse (JSON.stringify (pValeur));
  }

export function hub_id_manifest_game (pManifest, pPayload)
  {
    const tGame = pManifest && pManifest.game || {};
    return tGame.hub_id
      || tGame.projet_id
      || tGame._id
      || pPayload.game_id
      || tGame.id;
  }

function role_zoom_alternatif_depuis_asset (pAsset)
  {
    const tMeta = pAsset && pAsset.meta || {};
    if (typeof tMeta.role === 'string' && tMeta.role.trim ()) return tMeta.role.trim ();
    if (typeof pAsset.role === 'string' && pAsset.role.trim ()) return pAsset.role.trim ();
    const tKind = typeof (pAsset && pAsset.kind) === 'string' ? pAsset.kind : '';
    const tMatch = tKind.match (/^project_alt_zoom_svg:(.+)$/);
    return tMatch && tMatch[1] ? tMatch[1].trim () : null;
  }

function meta_hub_file_depuis_asset (pAsset, pPayload)
  {
    const tMeta = clone_simple_miroir (pAsset && pAsset.meta, {}) || {};
    const tKind = typeof (pAsset && pAsset.kind) === 'string' ? pAsset.kind : '';
    const tProjectId = hub_id_manifest_game (pPayload.manifest, pPayload);

    if (!tMeta.kind)
      {
        if (tKind === 'project_bubble_svg' || tKind === 'card_bubble_svg')
          tMeta.kind = 'project_bubble_svg';
        else if (tKind === 'project_alt_zoom_svg' || tKind.startsWith ('project_alt_zoom_svg:'))
          tMeta.kind = 'project_alt_zoom_svg';
      }

    if ((tMeta.kind === 'project_bubble_svg' || tMeta.kind === 'project_alt_zoom_svg')
        && !tMeta.projet_id && tProjectId)
      tMeta.projet_id = tProjectId;

    if (tMeta.kind === 'project_alt_zoom_svg' && !tMeta.role)
      {
        const tRole = role_zoom_alternatif_depuis_asset (pAsset);
        if (tRole) tMeta.role = tRole;
      }

    return tMeta;
  }

export function hub_file_doc_depuis_asset (pAsset, pPayload)
  {
    if (!pAsset || !pAsset.id || String (pAsset.id).includes (':')) return null;
    return {
      _id                  : pAsset.id,
      name                 : pAsset.name || pAsset.id,
      extension            : pAsset.extension || null,
      type                 : pAsset.content_type || null,
      size                 : pAsset.size || 0,
      url                  : pAsset.url || null,
      path_public          : pAsset.url || null,
      meta                 : meta_hub_file_depuis_asset (pAsset, pPayload),
      _diffusion_game_id   : pPayload.game_id,
      _diffusion_version_id: pPayload.version_id,
      _diffusion_asset_kind: pAsset.kind || 'asset',
    };
  }

// Construit tous les documents miroir d'un payload. Renvoie null si le
// payload ne permet pas d'identifier le projet.
export function docs_miroir_hub_depuis_payload (pPayload, pMaintenant)
  {
    const tManifest = pPayload.manifest;
    const tProjectId = hub_id_manifest_game (tManifest, pPayload);
    if (!tProjectId) return null;

    const tProjet = {
      _id                  : tProjectId,
      nom                  : tManifest.game.titre,
      slug                 : tManifest.game.slug,
      public_slug          : tManifest.game.public_slug || null,
      description          : tManifest.game.description || '',
      vignette_id          : tManifest.game.vignette_id || null,
      user_id              : tManifest.game.user_id || tManifest.game.owner_id || null,
      owner_id             : tManifest.game.owner_id || tManifest.game.user_id || null,
      rapport_lh_scene     : clone_simple_miroir (tManifest.game.rapport_lh_scene, {largeur : 16, hauteur : 9}),
      catalogue_diapos_taille_pct : normalise_catalogue_diapos_taille_pct (tManifest.game.catalogue_diapos_taille_pct),
      reglages_mode_lecture: clone_simple_miroir (tManifest.game.reglages_mode_lecture, {}),
      reglages_bulles      : clone_simple_miroir (tManifest.game.reglages_bulles, {}),
      ecran_demarrage      : clone_simple_miroir (tManifest.game.ecran_demarrage, {}),
      communication_diffusion: normalise_communication_diffusion_projet (tManifest.game.communication_diffusion),
      webapp               : clone_simple_miroir (tManifest.game.webapp, {}),
      audio_assets         : clone_simple_miroir (tManifest.game.audio_assets, {}),
      musique_ambiance     : clone_simple_miroir (tManifest.game.musique_ambiance, []),
      type                 : tManifest.game.type || 'diffusion-readonly',
      updatedAt            : pMaintenant,
      _diffusion_game_id   : pPayload.game_id,
      _diffusion_version_id: pPayload.version_id,
    };

    const tHubFiles = [];
    for (const tAsset of (Array.isArray (tManifest.assets) ? tManifest.assets : []))
      {
        const tDoc = hub_file_doc_depuis_asset (tAsset, pPayload);
        if (tDoc) tHubFiles.push (tDoc);
      }

    const tScenesDocs = [];
    const tSceneAccessDocs = [];
    const tCartesDocs = [];
    for (const tScene of (Array.isArray (tManifest.scenes) ? tManifest.scenes : []))
      {
        const tSceneId = tScene.hub_id || tScene._id || tScene.id;
        if (!tSceneId) continue;
        tScenesDocs.push (
          {
            _id                  : tSceneId,
            projet_id            : tProjectId,
            titre                : tScene.titre,
            slug                 : tScene.slug,
            ordre                : tScene.ordre,
            acces_libre_diffusion: !!tScene.acces_libre_diffusion,
            svg_id               : tScene.svg_id || null,
            svg_runtime_id       : Number (tScene.svg_runtime_version) === SVG_RUNTIME_COMPILATION_VERSION ? (tScene.svg_runtime_id || null) : null,
            svg_runtime_version  : Number (tScene.svg_runtime_version) === SVG_RUNTIME_COMPILATION_VERSION ? SVG_RUNTIME_COMPILATION_VERSION : null,
            vignette_id          : tScene.vignette_id || null,
            vue_defaut           : clone_simple_miroir (tScene.vue_defaut),
            svg_viewbox          : clone_simple_miroir (tScene.svg_viewbox),
            svg_objets           : clone_simple_miroir (tScene.svg_objets, {}),
            objets_config        : clone_simple_miroir (tScene.objets_config, []),
            audio_assets         : clone_simple_miroir (tScene.audio_assets, {}),
            mode_lecture_masque  : clone_simple_miroir (tScene.mode_lecture_masque, {}),
            ambiance_lecture     : clone_simple_miroir (tScene.ambiance_lecture, null),
            interface_style      : clone_simple_miroir (tScene.interface_style, {}),
            reglages_interface_carte : clone_simple_miroir (tScene.reglages_interface_carte, null),
            updatedAt            : pMaintenant,
            _diffusion_game_id   : pPayload.game_id,
            _diffusion_version_id: pPayload.version_id,
          }
        );
        tSceneAccessDocs.push (
          {
            _id                  : `${pPayload.version_id}:${tSceneId}`,
            version_id            : pPayload.version_id,
            game_id               : pPayload.game_id,
            project_id            : tProjectId,
            scene_id              : tSceneId,
            slug                  : tScene.slug || null,
            titre                 : tScene.titre || tScene.slug || '',
            ordre                 : tScene.ordre,
            acces_libre           : !!tScene.acces_libre_diffusion,
            _diffusion_game_id    : pPayload.game_id,
            _diffusion_version_id : pPayload.version_id,
          }
        );

        for (const tCarte of (Array.isArray (tScene.cartes) ? tScene.cartes : []))
          {
            const tCarteId = tCarte.hub_id || tCarte._id || tCarte.id;
            if (!tCarteId) continue;
            tCartesDocs.push (
              {
                _id                  : tCarteId,
                scene_id             : tSceneId,
                projet_id            : tProjectId,
                nom                  : tCarte.nom || tCarte.titre || '',
                ordre                : tCarte.ordre,
                vue                  : clone_simple_miroir (tCarte.vue),
                ids_masques          : clone_simple_miroir (tCarte.ids_masques, []),
                ordre_objets         : clone_simple_miroir (tCarte.ordre_objets, []),
                couleur_fond         : tCarte.couleur_fond || null,
                couleur_bulle        : tCarte.couleur_bulle || null,
                opacite_bulle        : tCarte.opacite_bulle,
                taille_bulle         : tCarte.taille_bulle,
                position_bulle       : clone_simple_miroir (tCarte.position_bulle),
                rotation_bulle       : tCarte.rotation_bulle,
                svg_bulle_id         : tCarte.svg_bulle_id || null,
                svg_bulle_url        : tCarte.svg_bulle_url || null,
                carte_fermeture_svg_id : tCarte.carte_fermeture_svg_id || null,
                carte_fermeture_svg_url : tCarte.carte_fermeture_svg_url || null,
                reglages_interface_carte : clone_simple_miroir (tCarte.reglages_interface_carte, null),
                bulle_ombre_asset_file_id : tCarte.bulle_ombre_asset_file_id || null,
                bulle_ombre_asset_file_name : tCarte.bulle_ombre_asset_file_name || null,
                bulle_ombre_asset_signature : tCarte.bulle_ombre_asset_signature || null,
                bulle_ombre_asset_url : tCarte.bulle_ombre_asset_url || null,
                updatedAt            : pMaintenant,
                _diffusion_game_id   : pPayload.game_id,
                _diffusion_version_id: pPayload.version_id,
              }
            );
          }
      }

    const tHypeDocs = [];
    for (const tWidget of (Array.isArray (tManifest.game.hype_widgets) ? tManifest.game.hype_widgets : []))
      {
        if (!tWidget || !tWidget.id) continue;
        tHypeDocs.push (
          {
            _id                  : tWidget.id,
            projet_id            : tProjectId,
            type                 : tWidget.type || 'widget',
            nom                  : tWidget.nom || 'Widget Hype',
            url                  : tWidget.url,
            vignette_url         : tWidget.vignette_url || null,
            updatedAt            : pMaintenant,
            _diffusion_game_id   : pPayload.game_id,
            _diffusion_version_id: pPayload.version_id,
          }
        );
      }

    return {
      projet_id    : tProjectId,
      projet       : tProjet,
      hub_files    : tHubFiles,
      scenes       : tScenesDocs,
      scene_access : tSceneAccessDocs,
      cartes       : tCartesDocs,
      hype_widgets : tHypeDocs,
    };
  }
